//third-party
import { Row, Col, Badge, Button as BsButton, Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";

//spotsync
import { PlanDetails } from "./Data/ApiTypes";
import { Input } from "./Controls/Input";
import { Select } from "./Controls/Select";
import { MediumPage } from "./Controls/MediumPage";
import { PageTitle } from "./Controls/PageTitle";
import { useParamIds } from "./Data/Common";
import { Button } from "./Controls/Button";
import { showMoney } from "./Data/Money";
import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, InfoCircleFill } from "react-bootstrap-icons";
import { MonitorFacilityApiApi, ServerTransferMonitorPlanUpdateReq } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";
import {
    fromApiLateFeeRules,
    lateFeeRulesAreValid,
    PlanLateFeeRulesSection,
    snapshotLateFeeRules,
    toApiLateFeeRules,
} from "./PlanLateFeeRulesSection";
import { CollapsibleHtmlField, PlanSectionCard, PlanSectionTabs, ProrateLabel, usePlanSectionNav } from "./PlanFormChrome";

const monitorApi = createApiService(MonitorFacilityApiApi);

export function FacilityPlanEditPage() {
    const nav = useNavigate();
    const [loading, setLoading] = useState( false );
    const [msg, setMsg] = useState<string>();
    const { facilityId, planId } = useParamIds();
    const plan   = useLoaderData() as PlanDetails;
    const [auth] = useAtom(authAtom);
    const canEditPlan = canPerformAction(extractClaims(auth), 'edit-plan');
    const prices = plan.prices;
    const isUsageBasedBilling = plan.isUsageBasedBilling;
    const isPrivateOrUsageBased = plan.isPrivate || plan.isUsageBasedBilling;
    const initialValues = useRef( {
        planEntryId: plan.planEntryId,
        name: plan.name ?? "",
        description: plan.description ?? "",
        accessHours: plan.accessHours ?? "",
        planPriceId: plan.planPriceId ?? null as number | null,
        dayOfMonthInvoice: plan.dayOfMonthInvoice,
        dayOfMonthDue: plan.dayOfMonthDue,
        note: "",
        prorate: plan.prorate ?? "",
        terms: plan.terms ?? "",
        approvalEmailText: plan.approvalEmailText ?? "",
        renewalFailureDeactivateAfterDays: plan.renewalFailureDeactivateAfterDays,
        cancellationWindowDays: plan.cancellationWindowDays ?? 0
    } as ServerTransferMonitorPlanUpdateReq & { planPriceId: number | null } );

    const [lateFeeRules, setLateFeeRules] = useState(() => fromApiLateFeeRules(plan.lateFeeRules));
    const lateFeeSnapshot = useRef(snapshotLateFeeRules(fromApiLateFeeRules(plan.lateFeeRules)));
    const [clearedLateFeesOnConversionOff, setClearedLateFeesOnConversionOff] = useState(false);
    const [lateFeeShowErrors, setLateFeeShowErrors] = useState(false);
    const [termsExpanded, setTermsExpanded] = useState(!canEditPlan);
    const [approvalExpanded, setApprovalExpanded] = useState(!canEditPlan);
    const { tabs, activeSection, jumpTo } = usePlanSectionNav(isUsageBasedBilling);

    const { register, handleSubmit, control, watch, setValue, clearErrors, formState: { errors } } = useForm<ServerTransferMonitorPlanUpdateReq>( {
        defaultValues: initialValues.current
    } );

    const isRenewalEnabled = watch("renewalFailureDeactivateAfterDays") > 0;
    const isCancellationWindowEnabled = (watch("cancellationWindowDays") ?? 0) > 0;

    const currentValues = watch();
    const stringKeys = [ "name", "description", "accessHours", "note", "prorate" ] as const;
    const num = (x: number | null | undefined) =>
  x ?? null;

    useEffect(() => {
        if (errors.terms) setTermsExpanded(true);
    }, [errors.terms]);

    const hasChanges = useMemo( () => {
        const a = initialValues.current;
        const b = currentValues;
        const str = (x: string | null | undefined ) => ( x ?? "" ).trim();
        const html = (x: string | null | undefined ) => ( x ?? "" ).replace( /<[^>]*>/g, " " ).replace( /\s+/g, " " ).trim();
        const priceId = (x: number | string | null | undefined ) => ( x === "" || x == null ? null : Number( x ) );
        const hasPriceChanges = !isPrivateOrUsageBased && priceId( a.planPriceId ) !== priceId( b.planPriceId );
        const hasTermsChanges = html( a.terms ) !== html( b.terms );
        const hasApprovalTextChanges = !isUsageBasedBilling && html( a.approvalEmailText ) !== html( b.approvalEmailText );
        const hasRenewalChanges = !isUsageBasedBilling &&
            num(a.renewalFailureDeactivateAfterDays) !==  num(b.renewalFailureDeactivateAfterDays);
        const hasCancellationWindowChanges = !isUsageBasedBilling &&
            num(a.cancellationWindowDays) !== num(b.cancellationWindowDays);
        const hasLateFeeChanges = snapshotLateFeeRules(lateFeeRules) !== lateFeeSnapshot.current;
        const keys = isUsageBasedBilling ? stringKeys.filter( k => k !== "accessHours" ) : stringKeys;
        return hasPriceChanges ||
            keys.some( ( k ) => str( a[k] ) !== str( b[k] ) ) ||
            hasTermsChanges ||
            hasApprovalTextChanges ||
            hasRenewalChanges ||
            hasCancellationWindowChanges ||
            hasLateFeeChanges;
    }, [ currentValues, isPrivateOrUsageBased, isUsageBasedBilling, lateFeeRules ] );

    const onSubmit = async ( data: ServerTransferMonitorPlanUpdateReq ) => {
        if (!isUsageBasedBilling && isRenewalEnabled && lateFeeRules.length > 0 && !lateFeeRulesAreValid(lateFeeRules)) {
            setLateFeeShowErrors(true);
            jumpTo("payments");
            return;
        }
        setLoading( true );
        if (isUsageBasedBilling || !isRenewalEnabled) data.renewalFailureDeactivateAfterDays = 0;
        if (isUsageBasedBilling || !isCancellationWindowEnabled) data.cancellationWindowDays = 0;
        data.lateFeeRules = isUsageBasedBilling || !isRenewalEnabled ? [] : toApiLateFeeRules(lateFeeRules);
        monitorApi.updatePlan({
            planId: planId,
            facilityId: facilityId,
            serverTransferMonitorPlanUpdateReq: data
        }).then((res)=>{
            setMsg( "Plan Updated Successfully" );
            nav( `/facility/${facilityId}/plan` );
        }).catch((err)=>{
            console.log(err)
            //TODO: move to toast
        }).finally(()=>{
             setLoading( false );
        })
    };

    return <MediumPage>
        <div className="plan-edit-sticky sticky-top">
            <div className="d-flex align-items-center gap-3">
                <BsButton variant="link" className="p-0" onClick={() => nav(-1)}>  ← Back</BsButton>
                <PageTitle>
                    {canEditPlan ? 'Edit' : 'View'} Plan "{plan.name}"
                </PageTitle>
            </div>
            {plan.isPrivate && !plan.isUsageBasedBilling && (
                <Badge className="fs-6 mb-2">Private Plan</Badge>
            )}
            {plan.isUsageBasedBilling && (
                <Badge className="fs-6 mb-2">Usage Based Plan</Badge>
            )}
            <PlanSectionTabs tabs={tabs} activeSection={activeSection} onJump={jumpTo} />
        </div>
        <form onSubmit={handleSubmit( onSubmit )}>
            <fieldset disabled={!canEditPlan}>
            <PlanSectionCard
                id="details"
                title="Details"
                description="Name and description shown to parkers in Connect."
            >
                <Input
                    type="text"
                    label="Name"
                    {...register( "name", { required: "Name is required" } )}
                    error={errors.name}
                />
                <Input
                    type="text"
                    label="Description"
                    as="textarea"
                    rows={2}
                    maxLength={256}
                    {...register("description", { required: "Description is required" })}
                    error={errors.description}
                />
            </PlanSectionCard>

            <PlanSectionCard
                id="content"
                title="Content"
                description={isUsageBasedBilling
                    ? "Legal terms parkers agree to."
                    : "Legal terms and the message sent when a subscription is approved."}
            >
                <Controller
                    name="terms"
                    control={control}
                    rules={{ required: "Terms and Conditions is required" }}
                    render={({ field }) => (
                        <CollapsibleHtmlField
                            label="Terms and Conditions"
                            value={field.value || ""}
                            onChange={field.onChange}
                            expanded={termsExpanded}
                            onToggle={() => setTermsExpanded((v) => !v)}
                            placeholder="Enter terms and conditions..."
                            error={errors.terms?.message}
                            canEdit={canEditPlan}
                        />
                    )}
                />
                {!isUsageBasedBilling && (
                    <Controller
                        name="approvalEmailText"
                        control={control}
                        render={({ field }) => (
                            <CollapsibleHtmlField
                                label="Approval Email Text"
                                hint="Optional message that appears in the email parkers receive when their subscription is approved."
                                value={field.value || ""}
                                onChange={field.onChange}
                                expanded={approvalExpanded}
                                onToggle={() => setApprovalExpanded((v) => !v)}
                                placeholder="Enter the approval email text..."
                                canEdit={canEditPlan}
                            />
                        )}
                    />
                )}
            </PlanSectionCard>

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="scheduling"
                title="Scheduling & price"
                description="When invoices go out, how proration works, access hours, and the listed price."
            >
                <Row>
                    <Col xs={12} md={4}>
                        <Input
                            type="number"
                            label="Invoice Day of Month"
                            icon={<Calendar />}
                            min={10}
                            max={20}
                            disabled
                            {...register( "dayOfMonthInvoice" )}
                            error={errors.dayOfMonthInvoice}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <Select
                            label={<ProrateLabel />}
                            required
                            min={1} max={28}
                            {...register( "prorate")}
                            error={errors.prorate}
                            disabled
                        >
                            <option value={'Daily'}>Daily</option>
                            <option value={'Monthly'}>1/2 Monthly</option>
                            <option value={''}>None</option>
                        </Select>
                    </Col>
                    <Col xs={12} md={4}>
                        <Input
                            type="text"
                            label="Access Hours"
                            placeholder="Mon - Fri 7am - 7pm"
                            maxLength={128}
                            {...register( "accessHours", { required: "Access hours are required" } )}
                            error={errors.accessHours}
                        />
                    </Col>
                </Row>
                <input type="hidden" {...register( "dayOfMonthDue" )} />
                {!isPrivateOrUsageBased && (
                    <Select
                        label="List Price"
                        explanation="This is the price displayed in search results in SpotSync Connect"
                        {...register( "planPriceId", { setValueAs: v => v === "" ? null : parseInt( v ) } )}>
                        <option value="">Unlisted</option>
                        {prices?.map( price => (
                            <option key={price.planPriceId}
                                    value={price.planPriceId}>
                                {showMoney( price.price )}
                            </option>
                        ) )}
                    </Select>
                )}
            </PlanSectionCard>
            )}

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="payments"
                title="Payments & fees"
                description="Failed-autopay conversion timing and late-fee tiers on unpaid invoices. Conversion timing determines when the first tier can fire."
            >
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="renewalFailureEnabled"
                        label="Convert to invoiced parker after failed payments"
                        checked={isRenewalEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("renewalFailureDeactivateAfterDays", 7);
                                setClearedLateFeesOnConversionOff(false);
                            } else {
                                setValue("renewalFailureDeactivateAfterDays", 0);
                                clearErrors("renewalFailureDeactivateAfterDays");
                                if (lateFeeRules.length > 0) {
                                    setLateFeeRules([]);
                                    setClearedLateFeesOnConversionOff(true);
                                }
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="renewalFailureDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="renewalFailureDays"
                            type="number"
                            min={1}
                            disabled={!isRenewalEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("renewalFailureDeactivateAfterDays", {
                                valueAsNumber: true,
                                validate: (v) => v === 0 || v >= 3 || "Must be at least 3 days when enabled"
                            })}
                            isInvalid={!!errors.renewalFailureDeactivateAfterDays}
                        />
                    </Form.Group>
                </div>
                {errors.renewalFailureDeactivateAfterDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.renewalFailureDeactivateAfterDays.message}
                    </Form.Text>
                )}
                <p className="plan-accent-note mb-0 mt-2">
                    {!isRenewalEnabled
                        ? "After 3 failed daily charges, the subscription stays on autopay. Turn this on to convert to invoiced billing after a grace period."
                        : <>
                            After 3 failed daily charges, the subscription converts to invoiced billing{" "}
                            <b>{watch("renewalFailureDeactivateAfterDays")} day(s)</b>{" "}
                            after the billing period starts. The account stays active and receives an invoice for the balance due.
                          </>}
                </p>
                {clearedLateFeesOnConversionOff && !isRenewalEnabled && (
                    <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 mt-2 small" role="alert">
                        <InfoCircleFill className="flex-shrink-0 mt-1" />
                        <span>
                            Late-fee tiers were cleared. Failed autopay will not generate an invoice, so late fees cannot apply.
                        </span>
                    </div>
                )}

                <PlanLateFeeRulesSection
                    conversionEnabled={isRenewalEnabled}
                    conversionDays={watch("renewalFailureDeactivateAfterDays") || 0}
                    rules={lateFeeRules}
                    onChange={setLateFeeRules}
                    disabled={!canEditPlan}
                    embedded
                    showErrors={lateFeeShowErrors}
                />
            </PlanSectionCard>
            )}

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="cancellation"
                title="Cancellation"
                description="Advance notice parkers must give when they cancel in Connect. Staff cancellations in Monitor are not affected."
            >
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="cancellationWindowEnabled"
                        label="Require cancellation notice"
                        checked={isCancellationWindowEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("cancellationWindowDays", 30);
                            } else {
                                setValue("cancellationWindowDays", 0);
                                clearErrors("cancellationWindowDays");
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="cancellationWindowDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="cancellationWindowDays"
                            type="number"
                            min={2}
                            disabled={!isCancellationWindowEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("cancellationWindowDays", {
                                valueAsNumber: true,
                                validate: (v) => v == null || v === 0 || v >= 2 || "Must be at least 2 days when enabled"
                            })}
                            isInvalid={!!errors.cancellationWindowDays}
                        />
                    </Form.Group>
                </div>
                {errors.cancellationWindowDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.cancellationWindowDays.message}
                    </Form.Text>
                )}
                <p className="plan-accent-note mb-0 mt-2">
                    {!isCancellationWindowEnabled
                        ? "Parkers in Connect can cancel effective immediately. No advance notice required."
                        : <>
                            Parkers in Connect must pick a cancellation date at least{" "}
                            <b>{watch("cancellationWindowDays")} day(s)</b>{" "}
                            from today. Staff cancellations in Monitor are not affected.
                          </>}
                </p>
            </PlanSectionCard>
            )}

            <Input
                type="text"
                label="Change Note"
                explanation="The reason for the change"
                as="textarea"
                rows={2}
                maxLength={512}
                {...register( "note" )}
                disabled={!hasChanges}
                error={errors.note}
            />
            </fieldset>

            <div className="d-flex justify-content-end gap-2 mb-3">
                <Button type="button" className={canEditPlan ? "w-50" : "w-100"} loading={loading} onClick={ () => nav( isUsageBasedBilling ? `/facility/${facilityId}/plan` : `/facility/${facilityId}/plan/${planId}` )}>
                    {canEditPlan ? 'Cancel' : 'Back'}
                </Button>
                {canEditPlan && (
                <Button type="submit" className="w-50 btn-main" loading={loading} disabled={!hasChanges}>
                    Save Changes
                </Button>
                )}
            </div>
        </form>
    </MediumPage>;
}
